import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../auth/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { apiGet } from '../services/api';
import { LayoutDashboard, Package, LogOut, Gift, Trophy, Award, Star, FileText } from 'lucide-react';
import GamificationNotification from '../components/GamificationNotification';
import ProgressBar from '../components/ProgressBar';

export default function UserDashboard() {
  const { user, isAuthenticated, logout, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [points, setPoints] = useState(0);
  const [pointsLoading, setPointsLoading] = useState(true);
  const [rank, setRank] = useState({ title: 'Bronze', points: 0, image_url: null, next_rank: null });
  const [rankLoading, setRankLoading] = useState(true);
  const [achievements, setAchievements] = useState({ earned: [], unearned: [], total_earned: 0, total_available: 0 });
  const [achievementsLoading, setAchievementsLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [currentUserPosition, setCurrentUserPosition] = useState<any>(null);
  const [season, setSeason] = useState<any>({
    number: 1,
    name: 'Season 1',
    start_date: null,
    end_date: null,
    image_url: null,
    days_remaining: null
  });
  const [seasonLoading, setSeasonLoading] = useState(true);
  const [rankRewards, setRankRewards] = useState<any[]>([]);
  const [rankRewardsLoading, setRankRewardsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  
  // Notification state
  const [notifications, setNotifications] = useState<any[]>([]);
  const [currentNotification, setCurrentNotification] = useState<any>(null);
  const [notificationOpen, setNotificationOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Generate anonymous codename for non-current users
  const generateCodename = (userId: number) => {
    const adjectives = ['Swift', 'Brave', 'Mighty', 'Shadow', 'Iron', 'Golden', 'Silent', 'Storm', 'Fierce', 'Noble', 'Dark', 'Mystic', 'Wild', 'Frozen', 'Crimson'];
    const animals = ['Falcon', 'Tiger', 'Dragon', 'Wolf', 'Bear', 'Phoenix', 'Panther', 'Eagle', 'Lion', 'Raven', 'Viper', 'Hawk', 'Lynx', 'Cobra', 'Serpent'];
    
    // Generate consistent hash from user ID
    const hash = userId * 2654435761; // Simple hash for consistent pseudonym
    const adjIndex = Math.abs(hash) % adjectives.length;
    const animalIndex = Math.abs(Math.floor(hash / adjectives.length)) % animals.length;
    
    return `${adjectives[adjIndex]} ${animals[animalIndex]}`;
  };

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        // Always fetch only 5 orders (last 5 orders)
        const response = await apiGet(`/user/orders?per_page=5`);
        if (response.success) {
          setOrders(response.orders);
        }
      } catch (err) {
        console.error('Failed to fetch orders:', err);
      } finally {
        setLoadingOrders(false);
      }
    };

    const fetchPoints = async () => {
      try {
        const response = await apiGet('/gamification/points');
        if (response.success) {
          setPoints(response.points);
        }
      } catch (err) {
        console.error('Failed to fetch points:', err);
      } finally {
        setPointsLoading(false);
      }
    };

    const fetchRank = async () => {
      try {
        const response = await apiGet('/gamification/rank');
        if (response.success) {
          setRank({
            title: response.rank.title || 'Bronze',
            points: response.rank.points || 0,
            image_url: response.rank.image_url || null,
            next_rank: response.rank.next_rank || null,
          });
        }
      } catch (err) {
        console.error('Failed to fetch rank:', err);
      } finally {
        setRankLoading(false);
      }
    };

    const fetchAchievements = async () => {
      try {
        const response = await apiGet('/gamification/achievements');
        if (response && response.success) {
          setAchievements(response.achievements);
        } else {
          console.error('Failed to fetch achievements: Invalid response', response);
        }
      } catch (err: any) {
        console.error('Failed to fetch achievements:', err);
        if (err.response) {
          console.error('Response status:', err.response.status);
          console.error('Response data:', err.response.data);
          console.error('Request URL:', err.config?.url);
        }
      } finally {
        setAchievementsLoading(false);
      }
    };

    const fetchLeaderboard = async () => {
      try {
        const response = await apiGet('/gamification/leaderboard?limit=10');
        if (response && response.success) {
          setLeaderboard(response.leaderboard || []);
          // ✅ Store current user position if they're not in top results
          setCurrentUserPosition(response.current_user_position || null);
        } else {
          console.error('Failed to fetch leaderboard: Invalid response', response);
        }
      } catch (err: any) {
        console.error('Failed to fetch leaderboard:', err);
      } finally {
        setLeaderboardLoading(false);
      }
    };

    const fetchSeason = async () => {
      try {
        const response = await apiGet('/gamification/season');
        if (response && response.success) {
          setSeason(response.season);
        } else {
          console.error('Failed to fetch season: Invalid response', response);
        }
      } catch (err: any) {
        console.error('Failed to fetch season:', err);
      } finally {
        setSeasonLoading(false);
      }
    };

    const fetchRankRewards = async () => {
      try {
        // Use the same base URL pattern as other API calls
        const baseUrl = process.env.REACT_APP_WORDPRESS_URL || process.env.REACT_APP_WORDPRESS_API_URL || 'https://oracle-gaming.co.za';
        // Ensure URL has protocol
        const apiBase = baseUrl.startsWith('http') ? baseUrl : `https://${baseUrl}`;
        // Remove trailing slash if present
        const cleanBase = apiBase.replace(/\/$/, '');
        const apiUrl = `${cleanBase}/wp-json/oracle/v1/gamification/ranks`;
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setRankRewards(data);
        } else {
          console.error('Failed to fetch rank rewards:', response.status, response.statusText);
        }
      } catch (err: any) {
        console.error('Failed to fetch rank rewards:', err);
      } finally {
        setRankRewardsLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchOrders();
      fetchPoints();
      fetchRank();
      fetchAchievements();
    }
    
    // Always fetch leaderboard, season, and rank rewards (public endpoints - work even when not authenticated)
    fetchLeaderboard();
    fetchSeason();
    fetchRankRewards();
  }, [isAuthenticated, location.pathname]);

  // Notification polling (every 10 seconds when authenticated)
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchNotifications = async () => {
      try {
        const response = await apiGet('/gamification/notifications');
        if (response.success && response.notifications && response.notifications.length > 0) {
          // Add new notifications to queue
          setNotifications(prev => [...prev, ...response.notifications]);
        }
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
      }
    };

    // Fetch immediately
    fetchNotifications();

    // Poll every 10 seconds
    const interval = setInterval(fetchNotifications, 10000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Handle notification queue - show one at a time
  useEffect(() => {
    if (!notificationOpen && notifications.length > 0) {
      const [next, ...rest] = notifications;
      setCurrentNotification(next);
      setNotifications(rest);
      setNotificationOpen(true);
    }
  }, [notificationOpen, notifications]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getPageTitle = () => {
    if (location.pathname.includes('/orders')) return 'MY ORDERS';
    if (location.pathname.includes('/leaderboard')) return 'LEADERBOARD';
    if (location.pathname.includes('/rank-rewards')) return 'RANK REWARDS';
    if (location.pathname.includes('/rules')) return 'RULES';
    return 'DASHBOARD';
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  if (authLoading) {
    return (
      <>
        <Header />
        <div className="h-20" />
        <section className="gallery-carousel" style={{ paddingTop: '120px', paddingBottom: '60px' }}>
          <div className="container">
            <div style={{ textAlign: 'center', color: 'var(--white)' }}>Loading...</div>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const initials = `${user?.firstName?.charAt(0) || ''}${user?.lastName?.charAt(0) || ''}`.toUpperCase();

  return (
    <>
      <Helmet>
        <title>{getPageTitle()} - Oracle Gaming</title>
        <meta name="description" content="Manage your Oracle Gaming account, view orders, and track your rewards." />
      </Helmet>
      <Header />
      <div className="h-20" />
      <section className="gallery-carousel" style={{ paddingTop: isMobile ? '140px' : '120px', paddingBottom: isMobile ? '40px' : '60px' }}>
        <div className="container">
          <h2 className="h2 section-title" style={{ color: 'var(--white)', marginBottom: isMobile ? '24px' : '40px' }}>
            {getPageTitle()}
          </h2>

          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '16px' : '24px',
            marginTop: '20px'
          }}>
            {/* Sidebar */}
            <div style={{
              position: 'relative',
              width: isMobile ? '100%' : '280px',
              flexShrink: 0,
            }}>

              <div className="bg-primarySupport shadow-lg border border-black/20" style={{ padding: '24px' }}>
                {/* User Profile Card */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '24px',
                  paddingBottom: '24px',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, var(--orange) 0%, var(--orange) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--white)',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    flexShrink: 0,
                  }}>
                    {initials}
                  </div>
                  <div style={{ marginLeft: '16px', minWidth: 0 }}>
                    <h3 style={{
                      color: 'var(--white)',
                      fontWeight: 'bold',
                      fontSize: '16px',
                      marginBottom: '4px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {user?.displayName}
                    </h3>
                    <p style={{
                      color: 'var(--platinum)',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {user?.email}
                    </p>
                  </div>
                </div>

                {/* Navigation */}
                <nav style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Link
                    to="/user/dashboard"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      color: isActive('/user/dashboard') ? 'var(--orange)' : 'var(--platinum)',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      transition: 'color 0.2s',
                      fontSize: '14px',
                      fontWeight: isActive('/user/dashboard') ? '600' : '400',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive('/user/dashboard')) {
                        e.currentTarget.style.color = 'var(--orange)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive('/user/dashboard')) {
                        e.currentTarget.style.color = 'var(--platinum)';
                      }
                    }}
                  >
                    <LayoutDashboard size={20} style={{ marginRight: '12px' }} color="currentColor" />
                    Dashboard
                  </Link>

                  <Link
                    to="/user/orders"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      color: isActive('/user/orders') ? 'var(--orange)' : 'var(--platinum)',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      transition: 'color 0.2s',
                      fontSize: '14px',
                      fontWeight: isActive('/user/orders') ? '600' : '400',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive('/user/orders')) {
                        e.currentTarget.style.color = 'var(--orange)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive('/user/orders')) {
                        e.currentTarget.style.color = 'var(--platinum)';
                      }
                    }}
                  >
                    <Package size={20} style={{ marginRight: '12px' }} color="currentColor" />
                    Orders
                  </Link>

                  <Link
                    to="/user/leaderboard"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      color: isActive('/user/leaderboard') ? 'var(--orange)' : 'var(--platinum)',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      transition: 'color 0.2s',
                      fontSize: '14px',
                      fontWeight: isActive('/user/leaderboard') ? '600' : '400',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive('/user/leaderboard')) {
                        e.currentTarget.style.color = 'var(--orange)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive('/user/leaderboard')) {
                        e.currentTarget.style.color = 'var(--platinum)';
                      }
                    }}
                  >
                    <Award size={20} style={{ marginRight: '12px' }} color="currentColor" />
                    Leaderboard
                  </Link>

                  <Link
                    to="/user/rank-rewards"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      color: isActive('/user/rank-rewards') ? 'var(--orange)' : 'var(--platinum)',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      transition: 'color 0.2s',
                      fontSize: '14px',
                      fontWeight: isActive('/user/rank-rewards') ? '600' : '400',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive('/user/rank-rewards')) {
                        e.currentTarget.style.color = 'var(--orange)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive('/user/rank-rewards')) {
                        e.currentTarget.style.color = 'var(--platinum)';
                      }
                    }}
                  >
                    <Star size={20} style={{ marginRight: '12px' }} color="currentColor" />
                    Rank Rewards
                  </Link>

                  <Link
                    to="/user/rules"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      color: isActive('/user/rules') ? 'var(--orange)' : 'var(--platinum)',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      transition: 'color 0.2s',
                      fontSize: '14px',
                      fontWeight: isActive('/user/rules') ? '600' : '400',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive('/user/rules')) {
                        e.currentTarget.style.color = 'var(--orange)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive('/user/rules')) {
                        e.currentTarget.style.color = 'var(--platinum)';
                      }
                    }}
                  >
                    <FileText size={20} style={{ marginRight: '12px' }} color="currentColor" />
                    Rules
                  </Link>

                  <button
                    onClick={handleLogout}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      padding: '12px 16px',
                      color: 'var(--platinum)',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      transition: 'color 0.2s',
                      fontSize: '14px',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'var(--orange)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'var(--platinum)';
                    }}
                  >
                    <LogOut size={20} style={{ marginRight: '12px' }} color="currentColor" />
                    Logout
                  </button>
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div style={{ flex: 1, minWidth: 0 }}>


              {/* Rewards Section - Only show on dashboard, not on orders, leaderboard, rank-rewards, or rules page */}
              {!location.pathname.includes('/orders') && !location.pathname.includes('/leaderboard') && !location.pathname.includes('/user/rank-rewards') && !location.pathname.includes('/rules') && (
                <div className="bg-primarySupport shadow-lg border border-black/20" style={{ padding: '24px', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                    <Trophy size={24} color="var(--orange)" style={{ marginRight: '12px' }} />
                    <h2 style={{ color: 'var(--white)', fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                      Your Rewards
                    </h2>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                    gap: '16px',
                    marginBottom: '20px',
                  }}>
                    <div style={{
                      background: 'var(--raisin-black-1)',
                      border: '1px solid rgba(20, 254, 251, 0.2)',
                      borderRadius: '4px',
                      padding: '20px',
                      textAlign: 'center',
                    }}>
                      <Gift size={32} color="var(--orange)" style={{ marginBottom: '12px' }} />
                      <h3 style={{ color: 'var(--white)', fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
                        XP
                      </h3>
                      <p style={{ color: 'var(--orange)', fontSize: '32px', fontWeight: 'bold', margin: 0 }}>
                        {pointsLoading ? '...' : points.toLocaleString()}
                      </p>
                      <p style={{ color: 'var(--platinum)', fontSize: '12px', marginTop: '8px', margin: 0 }}>
                        Earn XP with every purchase
                      </p>
                    </div>

                    <div style={{
                      background: 'var(--raisin-black-1)',
                      border: '1px solid rgba(20, 254, 251, 0.2)',
                      borderRadius: '4px',
                      padding: '20px',
                      textAlign: 'center',
                    }}>
                      {rank.image_url ? (
                        <img 
                          src={rank.image_url} 
                          alt={rank.title}
                          style={{
                            width: '64px',
                            height: '64px',
                            borderRadius: '4px',
                            objectFit: 'cover',
                            marginBottom: '12px',
                            margin: '0 auto 12px auto',
                            display: 'block'
                          }}
                        />
                      ) : (
                        <Trophy size={32} color="var(--orange)" style={{ marginBottom: '12px' }} />
                      )}
                      <h3 style={{ color: 'var(--white)', fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
                        Rank
                      </h3>
                      <p style={{ color: 'var(--orange)', fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                        {rankLoading ? '...' : rank.title}
                      </p>
                      
                      {/* Progress to Next Rank */}
                      {!rankLoading && rank.next_rank && (
                        <div style={{ marginTop: '16px', textAlign: 'left' }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '8px' 
                          }}>
                            <span style={{ color: 'var(--platinum)', fontSize: '12px' }}>
                              Next: {rank.next_rank.title}
                            </span>
                            <span style={{ color: 'var(--orange)', fontSize: '12px', fontWeight: 'bold' }}>
                              {rank.next_rank.points_needed} XP
                            </span>
                          </div>
                          <ProgressBar 
                            percentage={rank.next_rank.progress_percentage} 
                            height="8px"
                          />
                          <p style={{ 
                            color: 'var(--platinum)', 
                            fontSize: '11px', 
                            marginTop: '6px',
                            textAlign: 'center' 
                          }}>
                            {rank.next_rank.progress_percentage.toFixed(1)}% to next rank
                          </p>
                        </div>
                      )}
                      
                      {/* Max Rank Achieved */}
                      {!rankLoading && !rank.next_rank && rank.title !== 'Bronze' && (
                        <div style={{ marginTop: '12px' }}>
                          <span style={{ 
                            color: 'var(--orange)', 
                            fontSize: '12px',
                            fontWeight: 'bold',
                            padding: '4px 12px',
                            background: 'rgba(20, 254, 251, 0.1)',
                            borderRadius: '12px',
                            display: 'inline-block'
                          }}>
                            ⭐ Max Rank Achieved!
                          </span>
                        </div>
                      )}
                      
                      <p style={{ color: 'var(--platinum)', fontSize: '12px', marginTop: '8px', margin: 0 }}>
                        {rank.points > 0 ? `${rank.points.toLocaleString()} XP earned` : 'Start earning to level up!'}
                      </p>
                    </div>
                  </div>

                  {/* Achievements Section */}
                  <div style={{
                    background: 'var(--raisin-black-1)',
                    border: '1px solid rgba(20, 254, 251, 0.2)',
                    borderRadius: '4px',
                    padding: '16px',
                  }}>
                    <h3 style={{ color: 'var(--white)', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
                      Outstanding Achievements
                      {achievements.total_available > 0 && (
                        <span style={{ color: 'var(--platinum)', fontSize: '14px', fontWeight: 'normal', marginLeft: '8px' }}>
                          ({achievements.unearned.length}/{achievements.total_available})
                        </span>
                      )}
                    </h3>
                    <p style={{ 
                      color: 'var(--platinum)', 
                      fontSize: '12px', 
                      opacity: 0.7,
                      marginBottom: '12px',
                      fontStyle: 'italic'
                    }}>
                      Click on any achievement to see more details
                    </p>
                    
                    {achievementsLoading ? (
                      <p style={{ color: 'var(--platinum)', fontSize: '14px' }}>Loading achievements...</p>
                    ) : achievements.unearned.length === 0 && achievements.earned.length === 0 ? (
                      <p style={{ color: 'var(--platinum)', fontSize: '14px' }}>No achievements available yet.</p>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                        {/* Show ONLY unearned achievements - no earned achievements here */}
                        {achievements.unearned.length > 0 ? (
                          achievements.unearned.map((achievement) => (
                            <div
                              key={achievement.id}
                              onClick={() => navigate(`/achievement/${achievement.id}`)}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                minWidth: '100px',
                                maxWidth: '120px',
                                padding: '10px 8px',
                                background: 'rgba(20, 254, 251, 0.05)',
                                border: '1px solid rgba(20, 254, 251, 0.2)',
                                borderRadius: '4px',
                                opacity: 0.7,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = '1';
                                e.currentTarget.style.borderColor = 'var(--orange)';
                                e.currentTarget.style.background = 'rgba(20, 254, 251, 0.1)';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '0.7';
                                e.currentTarget.style.borderColor = 'rgba(20, 254, 251, 0.2)';
                                e.currentTarget.style.background = 'rgba(20, 254, 251, 0.05)';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                              title={achievement.description || achievement.title}
                            >
                              {achievement.image ? (
                                <img
                                  src={achievement.image}
                                  alt={achievement.title}
                                  style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '4px',
                                    objectFit: 'cover',
                                    marginBottom: '6px',
                                  }}
                                />
                              ) : (
                                <Trophy size={40} color="var(--platinum)" style={{ opacity: 0.5, marginBottom: '6px' }} />
                              )}
                              <span style={{ 
                                color: 'var(--platinum)', 
                                fontSize: '11px', 
                                fontWeight: '500',
                                textAlign: 'center',
                                lineHeight: '1.2',
                                marginBottom: '4px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                              }}>
                                {achievement.title}
                              </span>
                              {achievement.points > 0 && (
                                <span style={{ color: 'var(--orange)', fontSize: '10px', fontWeight: 'bold' }}>
                                  +{achievement.points}
                                </span>
                              )}
                            </div>
                          ))
                        ) : (
                          <p style={{ color: 'var(--platinum)', fontSize: '14px' }}>All achievements earned! 🎉</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Earned Achievements Section */}
                  {achievements.earned.length > 0 && (
                    <div style={{
                      background: 'var(--raisin-black-1)',
                      border: '1px solid rgba(20, 254, 251, 0.2)',
                      borderRadius: '4px',
                      padding: '16px',
                      marginTop: '16px',
                    }}>
                      <h3 style={{ color: 'var(--white)', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
                        Your Achievements
                        <span style={{ color: 'var(--platinum)', fontSize: '14px', fontWeight: 'normal', marginLeft: '8px' }}>
                          ({achievements.total_earned})
                        </span>
                      </h3>
                      <p style={{ 
                        color: 'var(--platinum)', 
                        fontSize: '12px', 
                        opacity: 0.7,
                        marginBottom: '12px',
                        fontStyle: 'italic'
                      }}>
                        Click on any achievement to see more details
                      </p>
                      
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                        {achievements.earned.map((achievement) => (
                          <div
                            key={achievement.id}
                            onClick={() => navigate(`/achievement/${achievement.id}`)}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              minWidth: '100px',
                              maxWidth: '120px',
                              padding: '10px 8px',
                              background: 'rgba(20, 254, 251, 0.1)',
                              border: '1px solid var(--orange)',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = 'var(--orange)';
                              e.currentTarget.style.background = 'rgba(20, 254, 251, 0.15)';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = 'var(--orange)';
                              e.currentTarget.style.background = 'rgba(20, 254, 251, 0.1)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                            title={achievement.description || achievement.title}
                          >
                            {achievement.image ? (
                              <img
                                src={achievement.image}
                                alt={achievement.title}
                                style={{
                                  width: '48px',
                                  height: '48px',
                                  borderRadius: '4px',
                                  objectFit: 'cover',
                                  marginBottom: '6px',
                                }}
                              />
                            ) : (
                              <Trophy size={40} color="var(--orange)" style={{ marginBottom: '6px' }} />
                            )}
                            <span style={{ 
                              color: 'var(--white)', 
                              fontSize: '11px', 
                              fontWeight: '500',
                              textAlign: 'center',
                              lineHeight: '1.2',
                              marginBottom: '4px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}>
                              {achievement.title}
                            </span>
                            {achievement.points > 0 && (
                              <span style={{ color: 'var(--orange)', fontSize: '10px', fontWeight: 'bold' }}>
                                +{achievement.points}
                              </span>
                            )}
                            {achievement.earned_date && (
                              <span style={{ 
                                color: 'var(--platinum)', 
                                fontSize: '9px', 
                                marginTop: '4px',
                                opacity: 0.7,
                              }}>
                                {new Date(achievement.earned_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Orders Section */}
              {location.pathname.includes('/orders') && (
                <div className="bg-primarySupport shadow-lg border border-black/20" style={{ padding: '24px' }}>
                  <h2 style={{ color: 'var(--white)', fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' }}>
                    All Orders
                  </h2>

                  {loadingOrders ? (
                    <p style={{ color: 'var(--platinum)' }}>Loading orders...</p>
                  ) : orders.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                      <p style={{ color: 'var(--platinum)', marginBottom: '20px' }}>No orders yet</p>
                      <Link to="/shop" className="btn btn-primary" style={{ display: 'inline-block' }}>
                        Start Shopping
                      </Link>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {orders.slice(0, 5).map((order) => (
                        <div
                          key={order.id}
                          className="bg-primary"
                          style={{
                            border: '1px solid rgba(20, 254, 251, 0.2)',
                            borderRadius: '4px',
                            padding: '16px',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = 'rgba(20, 254, 251, 0.4)';
                            e.currentTarget.style.background = 'rgba(20, 254, 251, 0.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = 'rgba(20, 254, 251, 0.2)';
                            e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '12px',
                            flexWrap: 'wrap',
                            gap: '8px',
                          }}>
                            <div>
                              <h3 style={{ color: 'var(--white)', fontWeight: 'bold', marginBottom: '4px' }}>
                                Order #{order.orderNumber}
                              </h3>
                              <p style={{ color: 'var(--platinum)', fontSize: '14px' }}>
                                {new Date(order.date).toLocaleDateString()}
                              </p>
                            </div>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              textTransform: 'capitalize',
                              ...(order.status === 'completed' ? {
                                background: 'rgba(76, 175, 80, 0.2)',
                                color: '#4caf50',
                              } : order.status === 'processing' ? {
                                background: 'rgba(33, 150, 243, 0.2)',
                                color: '#2196f3',
                              } : order.status === 'pending' ? {
                                background: 'rgba(255, 193, 7, 0.2)',
                                color: '#ffc107',
                              } : {
                                background: 'rgba(158, 158, 158, 0.2)',
                                color: '#9e9e9e',
                              })
                            }}>
                              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                            </span>
                          </div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                            flexWrap: 'wrap',
                            gap: '8px',
                          }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <p style={{ color: 'var(--platinum)', fontSize: '14px' }}>
                              {order.itemsCount} items
                            </p>
                              {order.pointsAwarded && order.pointsAwarded > 0 && (
                                <p style={{ 
                                  color: 'var(--orange)', 
                                  fontSize: '13px', 
                                  fontWeight: '600',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}>
                                  <span>+</span>
                                  <span>{order.pointsAwarded.toLocaleString()} XP</span>
                                </p>
                              )}
                            </div>
                            <p style={{ color: 'var(--white)', fontWeight: 'bold', fontSize: '18px' }}>
                              R{order.total}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Rank Rewards Section */}
              {location.pathname.includes('/user/rank-rewards') && (
                <div className="bg-primarySupport shadow-lg border border-black/20" style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                    <Star size={24} color="var(--orange)" style={{ marginRight: '12px' }} />
                    <h2 style={{ color: 'var(--white)', fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                      Rank Rewards
                    </h2>
                  </div>

                  <p style={{ color: 'var(--platinum)', fontSize: '14px', marginBottom: '24px', lineHeight: '1.6' }}>
                    Earn XP with every purchase and unlock exclusive rewards as you rank up!
                  </p>

                  {rankRewardsLoading ? (
                    <p style={{ color: 'var(--platinum)' }}>Loading rank rewards...</p>
                  ) : rankRewards.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                      <p style={{ color: 'var(--platinum)', marginBottom: '20px' }}>No ranks available at this time.</p>
                    </div>
                  ) : (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
                      gap: '24px',
                      marginTop: '20px'
                    }}>
                      {rankRewards.map((rank) => (
                        <div
                          key={rank.id}
                          style={{
                            background: 'var(--raisin-black-1)',
                            border: '2px solid rgba(20, 254, 251, 0.2)',
                            borderRadius: '8px',
                            padding: '20px',
                            transition: 'var(--transition-1)',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = 'var(--orange)';
                            e.currentTarget.style.transform = 'translateY(-5px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = 'rgba(20, 254, 251, 0.2)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          {/* Rank Header */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            marginBottom: '20px',
                            paddingBottom: '16px',
                            borderBottom: '1px solid rgba(20, 254, 251, 0.1)'
                          }}>
                            {rank.image_url ? (
                              <img
                                src={rank.image_url}
                                alt={rank.title}
                                style={{
                                  width: '64px',
                                  height: '64px',
                                  minWidth: '64px',
                                  minHeight: '64px',
                                  borderRadius: '8px',
                                  objectFit: 'cover',
                                  border: '2px solid var(--orange)',
                                  flexShrink: 0
                                }}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div style={{
                                width: '64px',
                                height: '64px',
                                background: 'rgba(20, 254, 251, 0.1)',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '32px',
                                border: '2px solid var(--orange)'
                              }}>
                                🏆
                              </div>
                            )}
                            <div style={{ flex: 1 }}>
                              <h3 style={{
                                margin: 0,
                                color: 'var(--white)',
                                fontSize: 'var(--fs-5)',
                                fontFamily: 'var(--ff-oswald)',
                                textTransform: 'uppercase',
                                letterSpacing: '1px'
                              }}>
                                {rank.title}
                              </h3>
                              {rank.points_required > 0 && (
                                <p style={{
                                  margin: '4px 0 0 0',
                                  color: 'var(--orange)',
                                  fontSize: 'var(--fs-9)',
                                  fontFamily: 'var(--ff-poppins)'
                                }}>
                                  {rank.points_required.toLocaleString()} XP Required
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Reward Section */}
                          {rank.reward.text || rank.reward.image_url ? (
                            <div style={{ marginTop: '20px' }}>
                              {rank.reward.image_url && (
                                <div style={{
                                  marginBottom: '16px',
                                  borderRadius: '8px',
                                  overflow: 'hidden',
                                  border: '1px solid rgba(20, 254, 251, 0.2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'rgba(20, 254, 251, 0.05)',
                                  minHeight: '200px',
                                  maxHeight: '400px'
                                }}>
                                  <img
                                    src={rank.reward.image_url}
                                    alt={`${rank.title} Reward`}
                                    style={{
                                      width: '100%',
                                      height: 'auto',
                                      maxHeight: '400px',
                                      objectFit: 'contain',
                                      display: 'block'
                                    }}
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                </div>
                              )}
                              {rank.reward.text && (() => {
                                // Split text by " | " to separate reward text from additional info
                                const parts = rank.reward.text.split(' | ');
                                const rewardText = parts[0] || '';
                                const additionalInfo = parts[1] || '';
                                
                                return (
                                  <div style={{
                                    padding: '16px',
                                    background: 'rgba(20, 254, 251, 0.05)',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(20, 254, 251, 0.1)'
                                  }}>
                                    {rewardText && (
                                      <p style={{
                                        margin: 0,
                                        marginBottom: additionalInfo ? '8px' : 0,
                                        color: 'var(--platinum)',
                                        fontSize: 'var(--fs-8)',
                                        lineHeight: '1.6',
                                        fontFamily: 'var(--ff-poppins)',
                                        fontWeight: 'bold'
                                      }}>
                                        {rewardText}
                                      </p>
                                    )}
                                    {additionalInfo && (
                                      <p style={{
                                        margin: 0,
                                        color: 'var(--platinum)',
                                        fontSize: 'var(--fs-8)',
                                        lineHeight: '1.6',
                                        fontFamily: 'var(--ff-poppins)',
                                        opacity: 0.8
                                      }}>
                                        {additionalInfo}
                                      </p>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          ) : (
                            <div style={{
                              padding: '16px',
                              textAlign: 'center',
                              color: 'var(--light-gray)',
                              fontSize: 'var(--fs-9)',
                              fontStyle: 'italic'
                            }}>
                              Reward details coming soon
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Leaderboard Section */}
              {location.pathname.includes('/leaderboard') && (
                <div className="bg-primarySupport shadow-lg border border-black/20" style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                    <Award size={24} color="var(--orange)" style={{ marginRight: '12px' }} />
                    <h2 style={{ color: 'var(--white)', fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                      Top Players
                    </h2>
                  </div>

                  {/* Season Badge */}
                  {!seasonLoading && season && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '16px',
                      background: 'rgba(20, 254, 251, 0.05)',
                      border: '1px solid rgba(20, 254, 251, 0.2)',
                      borderRadius: '4px',
                      marginBottom: '24px',
                      flexWrap: isMobile ? 'wrap' : 'nowrap'
                    }}>
                      {season.image_url && (
                        <img 
                          src={season.image_url} 
                          alt={season.name}
                          style={{
                            width: isMobile ? '40px' : '56px',
                            height: isMobile ? '40px' : '56px',
                            borderRadius: '4px',
                            objectFit: 'cover',
                            flexShrink: 0
                          }}
                        />
                      )}
                      {!season.image_url && (
                        <div style={{
                          width: isMobile ? '40px' : '56px',
                          height: isMobile ? '40px' : '56px',
                          borderRadius: '4px',
                          background: 'rgba(20, 254, 251, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: isMobile ? '20px' : '28px',
                          flexShrink: 0
                        }}>
                          🏆
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{
                          color: 'var(--orange)',
                          fontSize: isMobile ? '14px' : '16px',
                          fontWeight: 'bold',
                          margin: 0,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {season.name}
                        </h3>
                        {season.end_date && (
                          <p style={{
                            color: 'var(--platinum)',
                            fontSize: isMobile ? '11px' : '12px',
                            margin: '4px 0 0 0',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            Ends: {new Date(season.end_date).toLocaleDateString('en-US', {
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                        )}
                      </div>
                      {season.end_date && season.days_remaining !== null && (
                        <div style={{
                          textAlign: 'center',
                          padding: isMobile ? '8px 12px' : '10px 16px',
                          background: 'rgba(20, 254, 251, 0.1)',
                          border: '1px solid rgba(20, 254, 251, 0.3)',
                          borderRadius: '4px',
                          flexShrink: 0
                        }}>
                          <div style={{
                            color: 'var(--orange)',
                            fontSize: isMobile ? '18px' : '24px',
                            fontWeight: 'bold',
                            lineHeight: 1
                          }}>
                            {season.days_remaining}
                          </div>
                          <div style={{
                            color: 'var(--platinum)',
                            fontSize: isMobile ? '9px' : '10px',
                            textTransform: 'uppercase',
                            marginTop: '4px',
                            letterSpacing: '0.5px'
                          }}>
                            Days Left
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {leaderboardLoading ? (
                    <p style={{ color: 'var(--platinum)' }}>Loading leaderboard...</p>
                  ) : leaderboard.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                      <p style={{ color: 'var(--platinum)', marginBottom: '20px' }}>No players on the leaderboard yet</p>
                      <p style={{ color: 'var(--platinum)', fontSize: '14px' }}>
                        Start earning points to see your name here!
                      </p>
                    </div>
                  ) : isMobile ? (
                    // Mobile: Card-based layout
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {leaderboard.map((entry, index) => {
                        const isCurrentUser = entry.is_current_user;
                        
                        return (
                          <div
                            key={entry.user_id}
                            style={{
                              background: isCurrentUser 
                                ? 'rgba(20, 254, 251, 0.1)' 
                                : 'var(--raisin-black-1)',
                              border: isCurrentUser 
                                ? '1px solid var(--orange)' 
                                : '1px solid rgba(20, 254, 251, 0.2)',
                              borderRadius: '4px',
                              padding: '16px',
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              if (!isCurrentUser) {
                                e.currentTarget.style.borderColor = 'rgba(20, 254, 251, 0.4)';
                                e.currentTarget.style.background = 'rgba(20, 254, 251, 0.05)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isCurrentUser) {
                                e.currentTarget.style.borderColor = 'rgba(20, 254, 251, 0.2)';
                                e.currentTarget.style.background = 'var(--raisin-black-1)';
                              }
                            }}
                          >
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '12px',
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                              }}>
                                <span style={{
                                  color: isCurrentUser ? 'var(--orange)' : 'var(--white)',
                                  fontSize: '20px',
                                  fontWeight: 'bold',
                                  minWidth: '40px',
                                }}>
                                  #{entry.rank}
                                </span>
                                <div>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                  }}>
                                    <span style={{
                                      color: isCurrentUser ? 'var(--orange)' : 'var(--white)',
                                      fontSize: '16px',
                                      fontWeight: isCurrentUser ? 'bold' : '500',
                                    }}>
                                      {isCurrentUser ? entry.display_name : generateCodename(entry.user_id)}
                                    </span>
                                    {isCurrentUser && (
                                      <span style={{
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        background: 'rgba(20, 254, 251, 0.2)',
                                        color: 'var(--orange)',
                                        textTransform: 'uppercase',
                                      }}>
                                        You
                                      </span>
                                    )}
                                  </div>
                                  {entry.rank_title && (
                                    <span style={{
                                      display: 'inline-block',
                                      marginTop: '4px',
                                      padding: '4px 12px',
                                      borderRadius: '12px',
                                      fontSize: '11px',
                                      fontWeight: '600',
                                      background: 'rgba(20, 254, 251, 0.15)',
                                      color: 'var(--orange)',
                                      border: '1px solid rgba(20, 254, 251, 0.3)',
                                    }}>
                                      {entry.rank_title}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span style={{
                                color: isCurrentUser ? 'var(--orange)' : 'var(--white)',
                                fontSize: '20px',
                                fontWeight: 'bold',
                              }}>
                                {entry.points.toLocaleString()}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* ✅ Current User Position (if not in top results) - Mobile */}
                      {currentUserPosition && !leaderboard.find((e: any) => e.is_current_user) && (
                        <>
                          {/* Separator */}
                          <div style={{
                            margin: '20px 0',
                            textAlign: 'center',
                            position: 'relative',
                          }}>
                            <div style={{
                              height: '1px',
                              background: 'linear-gradient(to right, transparent, rgba(20, 254, 251, 0.3), transparent)',
                              margin: '0 20px',
                            }}></div>
                            <div style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              background: 'var(--dark-bg)',
                              padding: '0 16px',
                              color: 'var(--orange)',
                              fontSize: '12px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '1px',
                            }}>
                              Your Position
                            </div>
                          </div>
                          
                          {/* Current User Position Display - Mobile */}
                          <div style={{
                            background: 'rgba(20, 254, 251, 0.1)',
                            border: '2px solid rgba(20, 254, 251, 0.3)',
                            borderRadius: '8px',
                            padding: '16px',
                            marginTop: '16px',
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}>
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                              }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}>
                                  <span style={{
                                    color: 'var(--orange)',
                                    fontSize: '24px',
                                    fontWeight: 'bold',
                                  }}>
                                    #{currentUserPosition.rank}
                                  </span>
                                  <span style={{
                                    color: 'var(--orange)',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                  }}>
                                    {currentUserPosition.display_name}
                                  </span>
                                  <span style={{
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    background: 'rgba(20, 254, 251, 0.2)',
                                    color: 'var(--orange)',
                                    textTransform: 'uppercase',
                                  }}>
                                    You
                                  </span>
                                </div>
                                {currentUserPosition.rank_title && (
                                  <span style={{
                                    display: 'inline-block',
                                    marginTop: '4px',
                                    padding: '4px 12px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    background: 'rgba(20, 254, 251, 0.15)',
                                    color: 'var(--orange)',
                                    border: '1px solid rgba(20, 254, 251, 0.3)',
                                    width: 'fit-content',
                                  }}>
                                    {currentUserPosition.rank_title}
                                  </span>
                                )}
                              </div>
                              <span style={{
                                color: 'var(--orange)',
                                fontSize: '20px',
                                fontWeight: 'bold',
                              }}>
                                {currentUserPosition.points.toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    // Desktop: Table layout
                    <div style={{
                      overflowX: 'auto',
                      borderRadius: '4px',
                    }}>
                      <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                      }}>
                        <thead>
                          <tr style={{
                            borderBottom: '2px solid rgba(20, 254, 251, 0.3)',
                          }}>
                            <th style={{
                              padding: '12px 16px',
                              textAlign: 'left',
                              color: 'var(--orange)',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}>Rank</th>
                            <th style={{
                              padding: '12px 16px',
                              textAlign: 'left',
                              color: 'var(--orange)',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}>Player</th>
                            <th style={{
                              padding: '12px 16px',
                              textAlign: 'right',
                              color: 'var(--orange)',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}>Points</th>
                            <th style={{
                              padding: '12px 16px',
                              textAlign: 'left',
                              color: 'var(--orange)',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}>Rank</th>
                          </tr>
                        </thead>
                        <tbody>
                          {leaderboard.map((entry, index) => {
                            const isCurrentUser = entry.is_current_user;
                            
                            return (
                              <tr
                                key={entry.user_id}
                                style={{
                                  borderBottom: '1px solid rgba(20, 254, 251, 0.1)',
                                  background: isCurrentUser 
                                    ? 'rgba(20, 254, 251, 0.1)' 
                                    : index % 2 === 0 
                                      ? 'rgba(255, 255, 255, 0.02)' 
                                      : 'transparent',
                                  transition: 'all 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                  if (!isCurrentUser) {
                                    e.currentTarget.style.background = 'rgba(20, 254, 251, 0.05)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isCurrentUser) {
                                    e.currentTarget.style.background = index % 2 === 0 
                                      ? 'rgba(255, 255, 255, 0.02)' 
                                      : 'transparent';
                                  }
                                }}
                              >
                                <td style={{
                                  padding: '16px',
                                  color: isCurrentUser ? 'var(--orange)' : 'var(--white)',
                                  fontSize: '18px',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  width: '80px',
                                }}>
                                  #{entry.rank}
                                </td>
                                <td style={{
                                  padding: '16px',
                                }}>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                  }}>
                                    <span style={{
                                      color: isCurrentUser ? 'var(--orange)' : 'var(--white)',
                                      fontSize: '16px',
                                      fontWeight: isCurrentUser ? 'bold' : '500',
                                    }}>
                                      {isCurrentUser ? entry.display_name : generateCodename(entry.user_id)}
                                    </span>
                                    {isCurrentUser && (
                                      <span style={{
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        background: 'rgba(20, 254, 251, 0.2)',
                                        color: 'var(--orange)',
                                        textTransform: 'uppercase',
                                      }}>
                                        You
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td style={{
                                  padding: '16px',
                                  textAlign: 'right',
                                }}>
                                  <span style={{
                                    color: isCurrentUser ? 'var(--orange)' : 'var(--white)',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                  }}>
                                    {entry.points.toLocaleString()}
                                  </span>
                                </td>
                                <td style={{
                                  padding: '16px',
                                }}>
                                  {entry.rank_title ? (
                                    <span style={{
                                      padding: '4px 12px',
                                      borderRadius: '12px',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      background: 'rgba(20, 254, 251, 0.15)',
                                      color: 'var(--orange)',
                                      border: '1px solid rgba(20, 254, 251, 0.3)',
                                    }}>
                                      {entry.rank_title}
                                    </span>
                                  ) : (
                                    <span style={{
                                      color: 'var(--platinum)',
                                      fontSize: '12px',
                                      fontStyle: 'italic',
                                    }}>
                                      -
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {/* ✅ Current User Position (if not in top results) */}
                  {currentUserPosition && !leaderboard.find((e: any) => e.is_current_user) && (
                    <>
                      {/* Separator */}
                      <div style={{
                        margin: '20px 0',
                        textAlign: 'center',
                        position: 'relative',
                      }}>
                        <div style={{
                          height: '1px',
                          background: 'linear-gradient(to right, transparent, rgba(20, 254, 251, 0.3), transparent)',
                          margin: '0 20px',
                        }}></div>
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          background: 'var(--dark-bg)',
                          padding: '0 16px',
                          color: 'var(--orange)',
                          fontSize: '12px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                        }}>
                          Your Position
                </div>
                      </div>
                      
                      {/* Current User Position Display */}
                      {isMobile ? (
                        // Mobile: Card layout
                        <div style={{
                          background: 'rgba(20, 254, 251, 0.1)',
                          border: '2px solid rgba(20, 254, 251, 0.3)',
                          borderRadius: '8px',
                          padding: '16px',
                          marginTop: '16px',
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}>
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '8px',
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                              }}>
                                <span style={{
                                  color: 'var(--orange)',
                                  fontSize: '24px',
                                  fontWeight: 'bold',
                                }}>
                                  #{currentUserPosition.rank}
                                </span>
                                <span style={{
                                  color: 'var(--orange)',
                                  fontSize: '16px',
                                  fontWeight: 'bold',
                                }}>
                                  {currentUserPosition.display_name}
                                </span>
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '10px',
                                  fontWeight: 'bold',
                                  background: 'rgba(20, 254, 251, 0.2)',
                                  color: 'var(--orange)',
                                  textTransform: 'uppercase',
                                }}>
                                  You
                                </span>
                              </div>
                              {currentUserPosition.rank_title && (
                                <span style={{
                                  display: 'inline-block',
                                  marginTop: '4px',
                                  padding: '4px 12px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  background: 'rgba(20, 254, 251, 0.15)',
                                  color: 'var(--orange)',
                                  border: '1px solid rgba(20, 254, 251, 0.3)',
                                  width: 'fit-content',
                                }}>
                                  {currentUserPosition.rank_title}
                                </span>
                              )}
                            </div>
                            <span style={{
                              color: 'var(--orange)',
                              fontSize: '20px',
                              fontWeight: 'bold',
                            }}>
                              {currentUserPosition.points.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ) : (
                        // Desktop: Table row
                        <div style={{
                          overflowX: 'auto',
                          borderRadius: '4px',
                          marginTop: '20px',
                        }}>
                          <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                          }}>
                            <tbody>
                              <tr style={{
                                borderTop: '2px solid rgba(20, 254, 251, 0.3)',
                                background: 'rgba(20, 254, 251, 0.1)',
                              }}>
                                <td style={{
                                  padding: '16px',
                                  color: 'var(--orange)',
                                  fontSize: '18px',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  width: '80px',
                                }}>
                                  #{currentUserPosition.rank}
                                </td>
                                <td style={{
                                  padding: '16px',
                                }}>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                  }}>
                                    <span style={{
                                      color: 'var(--orange)',
                                      fontSize: '16px',
                                      fontWeight: 'bold',
                                    }}>
                                      {currentUserPosition.display_name}
                                    </span>
                                    <span style={{
                                      padding: '2px 8px',
                                      borderRadius: '12px',
                                      fontSize: '10px',
                                      fontWeight: 'bold',
                                      background: 'rgba(20, 254, 251, 0.2)',
                                      color: 'var(--orange)',
                                      textTransform: 'uppercase',
                                    }}>
                                      You
                                    </span>
                                  </div>
                                </td>
                                <td style={{
                                  padding: '16px',
                                  textAlign: 'right',
                                }}>
                                  <span style={{
                                    color: 'var(--orange)',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                  }}>
                                    {currentUserPosition.points.toLocaleString()}
                                  </span>
                                </td>
                                <td style={{
                                  padding: '16px',
                                }}>
                                  {currentUserPosition.rank_title ? (
                                    <span style={{
                                      padding: '4px 12px',
                                      borderRadius: '12px',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      background: 'rgba(20, 254, 251, 0.15)',
                                      color: 'var(--orange)',
                                      border: '1px solid rgba(20, 254, 251, 0.3)',
                                    }}>
                                      {currentUserPosition.rank_title}
                                    </span>
                                  ) : (
                                    <span style={{
                                      color: 'var(--platinum)',
                                      fontSize: '12px',
                                      fontStyle: 'italic',
                                    }}>
                                      -
                                    </span>
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Rules Section */}
              {location.pathname.includes('/rules') && (
                <div className="bg-primarySupport shadow-lg border border-black/20" style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                    <FileText size={24} color="var(--orange)" style={{ marginRight: '12px' }} />
                    <h2 style={{ color: 'var(--white)', fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                      Rewards System Rules
                    </h2>
                  </div>

                  <div style={{
                    color: 'var(--platinum)',
                    fontSize: '14px',
                    lineHeight: '1.8',
                    fontFamily: 'var(--ff-poppins)'
                  }}>
                    {/* XP Earning Rules */}
                    <div style={{
                      background: 'rgba(20, 254, 251, 0.05)',
                      border: '1px solid rgba(20, 254, 251, 0.2)',
                      borderRadius: '8px',
                      padding: '20px',
                      marginBottom: '20px'
                    }}>
                      <h3 style={{
                        color: 'var(--orange)',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Gift size={20} color="var(--orange)" />
                        Earning XP (Experience Points)
                      </h3>
                      <ul style={{
                        margin: 0,
                        paddingLeft: '20px',
                        listStyle: 'disc'
                      }}>
                        <li style={{ marginBottom: '12px' }}>
                          <strong style={{ color: 'var(--white)' }}>Automatic Purchase Rewards:</strong> XP is automatically awarded when you complete a purchase. You earn <strong style={{ color: 'var(--orange)' }}>10 XP for every 1 Rand spent</strong> on your order.
                        </li>
                        <li style={{ marginBottom: '12px' }}>
                          <strong style={{ color: 'var(--white)' }}>Achievement Completion:</strong> XP is also awarded when you complete achievements. Each achievement has its own XP reward value.
                        </li>
                        <li style={{ marginBottom: '0' }}>
                          <strong style={{ color: 'var(--white)' }}>Achievement Verification:</strong> If an achievement requires you to complete an action or task outside of the website (e.g., sharing on social media, attending an event), you can send a picture or screenshot as proof to:
                          <div style={{
                            marginTop: '12px',
                            padding: '12px',
                            background: 'rgba(20, 254, 251, 0.1)',
                            borderRadius: '4px',
                            border: '1px solid rgba(20, 254, 251, 0.2)'
                          }}>
                            <p style={{ margin: '4px 0', color: 'var(--white)' }}>
                              <strong>WhatsApp:</strong> <a href="https://wa.me/27825655408" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--orange)', textDecoration: 'none' }}>+27 82 565 5408</a>
                            </p>
                            <p style={{ margin: '4px 0', color: 'var(--white)' }}>
                              <strong>Email:</strong> <a href="mailto:oraclegaming.za@gmail.com" style={{ color: 'var(--orange)', textDecoration: 'none' }}>oraclegaming.za@gmail.com</a>
                            </p>
                          </div>
                        </li>
                      </ul>
                    </div>

                    {/* Rank Progression */}
                    <div style={{
                      background: 'rgba(20, 254, 251, 0.05)',
                      border: '1px solid rgba(20, 254, 251, 0.2)',
                      borderRadius: '8px',
                      padding: '20px',
                      marginBottom: '20px'
                    }}>
                      <h3 style={{
                        color: 'var(--orange)',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Trophy size={20} color="var(--orange)" />
                        Rank Progression
                      </h3>
                      <ul style={{
                        margin: 0,
                        paddingLeft: '20px',
                        listStyle: 'disc'
                      }}>
                        <li style={{ marginBottom: '12px' }}>
                          <strong style={{ color: 'var(--white)' }}>Automatic Rank Up:</strong> Your rank automatically increases when you reach the required XP threshold for the next rank.
                        </li>
                        <li style={{ marginBottom: '0' }}>
                          <strong style={{ color: 'var(--white)' }}>Rank Requirements:</strong> Each rank requires a specific amount of XP to unlock. You can view all available ranks and their requirements in the <Link to="/user/rank-rewards" style={{ color: 'var(--orange)', textDecoration: 'none' }}>Rank Rewards</Link> section.
                        </li>
                      </ul>
                    </div>

                    {/* Reward Distribution */}
                    <div style={{
                      background: 'rgba(20, 254, 251, 0.05)',
                      border: '1px solid rgba(20, 254, 251, 0.2)',
                      borderRadius: '8px',
                      padding: '20px',
                      marginBottom: '20px'
                    }}>
                      <h3 style={{
                        color: 'var(--orange)',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Star size={20} color="var(--orange)" />
                        Reward Distribution
                      </h3>
                      <ul style={{
                        margin: 0,
                        paddingLeft: '20px',
                        listStyle: 'disc'
                      }}>
                        <li style={{ marginBottom: '12px' }}>
                          <strong style={{ color: 'var(--white)' }}>Available Rewards:</strong> All available prizes and rewards can be found in the <Link to="/user/rank-rewards" style={{ color: 'var(--orange)', textDecoration: 'none' }}>Rank Rewards</Link> section.
                        </li>
                        <li style={{ marginBottom: '12px' }}>
                          <strong style={{ color: 'var(--white)' }}>Reward Shipping:</strong> Rank rewards will be awarded to players and shipped to them:
                          <ul style={{
                            marginTop: '8px',
                            paddingLeft: '20px',
                            listStyle: 'circle'
                          }}>
                            <li style={{ marginBottom: '8px' }}>
                              <strong style={{ color: 'var(--white)' }}>During Current Order:</strong> If your current order triggered the reward, it will be included in that shipment.
                            </li>
                            <li style={{ marginBottom: '0' }}>
                              <strong style={{ color: 'var(--white)' }}>Next Subsequent Order:</strong> If you've already placed an order, the reward will be included in your next order's shipment.
                            </li>
                          </ul>
                        </li>
                        <li style={{ marginBottom: '0' }}>
                          <strong style={{ color: 'var(--white)' }}>Leaderboard Rewards:</strong> Season leaderboard winners will receive their rewards at the end of each season. Rewards will be shipped with your next order or separately if you have no pending orders.
                        </li>
                      </ul>
                    </div>

                    {/* Leaderboard Privacy */}
                    <div style={{
                      background: 'rgba(20, 254, 251, 0.05)',
                      border: '1px solid rgba(20, 254, 251, 0.2)',
                      borderRadius: '8px',
                      padding: '20px',
                      marginBottom: '20px'
                    }}>
                      <h3 style={{
                        color: 'var(--orange)',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Award size={20} color="var(--orange)" />
                        Leaderboard Privacy
                      </h3>
                      <p style={{ margin: 0 }}>
                        <strong style={{ color: 'var(--white)' }}>Privacy Protection:</strong> All leaderboard names are hidden for privacy, except for your own name. You will see your actual name highlighted, while other players are displayed with anonymous codenames.
                      </p>
                    </div>

                    {/* General Rules */}
                    <div style={{
                      background: 'rgba(20, 254, 251, 0.05)',
                      border: '1px solid rgba(20, 254, 251, 0.2)',
                      borderRadius: '8px',
                      padding: '20px',
                      marginBottom: '20px'
                    }}>
                      <h3 style={{
                        color: 'var(--orange)',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <FileText size={20} color="var(--orange)" />
                        General Rules
                      </h3>
                      <ul style={{
                        margin: 0,
                        paddingLeft: '20px',
                        listStyle: 'disc'
                      }}>
                        <li style={{ marginBottom: '12px' }}>
                          <strong style={{ color: 'var(--white)' }}>Season Reset:</strong> XP, ranks, and leaderboard positions reset at the end of each season. Your progress starts fresh for the new season.
                        </li>
                        <li style={{ marginBottom: '12px' }}>
                          <strong style={{ color: 'var(--white)' }}>Account Required:</strong> You must have a registered account to earn XP and participate in the rewards system. Guest checkout orders do not earn XP.
                        </li>
                        <li style={{ marginBottom: '12px' }}>
                          <strong style={{ color: 'var(--white)' }}>XP Calculation:</strong> XP is calculated based on the final order total after discounts and shipping. Refunded orders will have their XP deducted.
                        </li>
                        <li style={{ marginBottom: '12px' }}>
                          <strong style={{ color: 'var(--white)' }}>Reward Eligibility:</strong> You must maintain an active account in good standing to be eligible for rewards. Accounts with payment issues or policy violations may be disqualified.
                        </li>
                        <li style={{ marginBottom: '12px' }}>
                          <strong style={{ color: 'var(--white)' }}>Reward Availability:</strong> Rewards are subject to availability. If a specific reward is out of stock, we will contact you to arrange an alternative reward of equal value.
                        </li>
                        <li style={{ marginBottom: '0' }}>
                          <strong style={{ color: 'var(--white)' }}>Dispute Resolution:</strong> If you have any questions or concerns about your XP, ranks, or rewards, please contact us via WhatsApp or email (listed above) and we'll be happy to assist you.
                        </li>
                      </ul>
                    </div>

                    {/* Contact Information */}
                    <div style={{
                      background: 'rgba(20, 254, 251, 0.1)',
                      border: '2px solid rgba(20, 254, 251, 0.3)',
                      borderRadius: '8px',
                      padding: '20px',
                      textAlign: 'center'
                    }}>
                      <h3 style={{
                        color: 'var(--orange)',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        marginBottom: '16px'
                      }}>
                        Need Help?
                      </h3>
                      <p style={{ margin: '8px 0', color: 'var(--white)' }}>
                        <strong>WhatsApp:</strong> <a href="https://wa.me/27825655408" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--orange)', textDecoration: 'none' }}>+27 82 565 5408</a>
                      </p>
                      <p style={{ margin: '8px 0', color: 'var(--white)' }}>
                        <strong>Email:</strong> <a href="mailto:oraclegaming.za@gmail.com" style={{ color: 'var(--orange)', textDecoration: 'none' }}>oraclegaming.za@gmail.com</a>
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
      
      {/* Gamification Notification */}
      {currentNotification && (
        <GamificationNotification
          notification={currentNotification}
          isOpen={notificationOpen}
          onClose={() => setNotificationOpen(false)}
        />
      )}
    </>
  );
}

